import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, A11y } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";

function BannerOne() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create React App ortam değişkeni
  const API_BASE_URL = (
    process.env.REACT_APP_API_BASE_URL || ""
  ).replace(/\/+$/, "");

const getImageUrl = (image) => {
  if (!image) return "";

  let imagePath = image;

  // Görsel nesne veya dizi olarak gelirse yolu çıkar
  if (typeof image === "object") {
    if (Array.isArray(image)) {
      imagePath =
        image[0]?.url ||
        image[0]?.path ||
        image[0]?.src ||
        "";
    } else {
      imagePath =
        image.url ||
        image.path ||
        image.src ||
        "";
    }
  }

  if (!imagePath || typeof imagePath !== "string") {
    return "";
  }

  // Zaten tam URL olarak geliyorsa değiştirme
  if (
    imagePath.startsWith("http://") ||
    imagePath.startsWith("https://") ||
    imagePath.startsWith("data:") ||
    imagePath.startsWith("blob:")
  ) {
    return imagePath;
  }

  // Yol zaten /api/cdn içeriyorsa tekrar ekleme
  if (imagePath.startsWith("/cdn/")) {
    return `${API_BASE_URL}${imagePath}`;
  }

  // Baştaki gereksiz slash karakterlerini temizle
  const cleanImagePath = imagePath.replace(/^\/+/, "");

  return `${API_BASE_URL}/cdn/${cleanImagePath}`;
};

  useEffect(() => {
    const controller = new AbortController();

    const fetchSliderData = async () => {
      try {
        setLoading(true);

        const searchParams = new URLSearchParams(
          window.location.search
        );

        const currentLang =
          searchParams.get("lang") || "tr";

        const response = await fetch(
          `${API_BASE_URL}/api/slider?lang=${encodeURIComponent(
            currentLang
          )}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          throw new Error(
            `Slider verisi alınamadı: ${response.status}`
          );
        }

        const data = await response.json();

        setSlides(Array.isArray(data) ? data : []);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("SLIDER_API_ERROR:", error);
          setSlides([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchSliderData();

    return () => {
      controller.abort();
    };
  }, [API_BASE_URL]);

  const renderSlide = (slide, index) => {
    const phoneImage = getImageUrl(slide.phoneImage);
    const tabletImage = getImageUrl(slide.tabletImage);
    const computerImage = getImageUrl(slide.computerImage);

    const fallbackImage =
      computerImage || tabletImage || phoneImage;

    if (!fallbackImage) return null;

    const hasButton =
      Boolean(slide.buttonText) &&
      Boolean(slide.buttonLink);

    const hasFullSlideLink =
      Boolean(slide.buttonLink) &&
      !slide.buttonText;

    const hasTitle =
      slide.title ||
      slide.colorfulText ||
      slide.textFinal;

    const hasContent =
      hasTitle ||
      slide.shortDescription ||
      slide.editor ||
      hasButton;

    const slideContent = (
      <div
        className={`demirsan-banner-slide ${
          hasFullSlideLink
            ? "demirsan-banner-slide-clickable"
            : ""
        }`}
      >
        <picture className="demirsan-banner-picture">
          {phoneImage && (
            <source
              media="(max-width: 767px)"
              srcSet={phoneImage}
            />
          )}

          {tabletImage && (
            <source
              media="(min-width: 768px) and (max-width: 1199px)"
              srcSet={tabletImage}
            />
          )}

          <img
            src={fallbackImage}
            alt={
              slide.title ||
              `Demirsan Slider ${index + 1}`
            }
            loading={index === 0 ? "eager" : "lazy"}
          />
        </picture>

        {hasContent && (
          <div className="demirsan-banner-overlay">
            <div className="container">
              <div className="demirsan-banner-content">
                {hasTitle && (
                  <h2 className="demirsan-banner-title">
                    {slide.title && (
                      <span>{slide.title}</span>
                    )}

                    {slide.colorfulText && (
                      <span className="demirsan-banner-colorful-text">
                        {slide.colorfulText}
                      </span>
                    )}

                    {slide.textFinal && (
                      <span>{slide.textFinal}</span>
                    )}
                  </h2>
                )}

                {slide.shortDescription && (
                  <p className="demirsan-banner-description">
                    {slide.shortDescription}
                  </p>
                )}

                {slide.editor && (
                  <div
                    className="demirsan-banner-editor"
                    dangerouslySetInnerHTML={{
                      __html: slide.editor,
                    }}
                  />
                )}

                {hasButton && (
                  <a
                    className="demirsan-banner-button"
                    href={slide.buttonLink}
                    target={slide.buttonTarget || "_self"}
                    rel={
                      slide.buttonTarget === "_blank"
                        ? "noopener noreferrer"
                        : undefined
                    }
                  >
                    {slide.buttonText}
                    <i className="far fa-arrow-right" />
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );

    // Buton yazısı yok ama link varsa bütün slider tıklanabilir
    if (hasFullSlideLink) {
      return (
        <a
          className="demirsan-banner-full-link"
          href={slide.buttonLink}
          target={slide.buttonTarget || "_self"}
          rel={
            slide.buttonTarget === "_blank"
              ? "noopener noreferrer"
              : undefined
          }
          aria-label={
            slide.title || "Slider bağlantısını aç"
          }
        >
          {slideContent}
        </a>
      );
    }

    return slideContent;
  };

  if (loading) {
    return (
      <div className="demirsan-banner-loading" />
    );
  }

  if (slides.length === 0) {
    return null;
  }

  return (
    <div>
      <style>
        {`
          .demirsan-banner {
            position: relative;
            width: 100%;
            overflow: hidden;
          }

          .demirsan-banner .swiper {
            width: 100%;
          }

          .demirsan-banner-slide {
            position: relative;
            width: 100%;
            height: 650px;
            overflow: hidden;
            background: #111111;
          }

          .demirsan-banner-picture {
            display: block;
            width: 100%;
            height: 100%;
          }

          .demirsan-banner-picture img {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
          }

          .demirsan-banner-full-link {
            display: block;
            width: 100%;
            color: inherit !important;
            text-decoration: none !important;
          }

          .demirsan-banner-full-link::before,
          .demirsan-banner-full-link::after {
            display: none !important;
            content: none !important;
          }

          .demirsan-banner-slide-clickable {
            cursor: pointer;
          }

          .demirsan-banner-overlay {
            position: absolute;
            inset: 0;

            display: flex;
            align-items: center;

            background:
              linear-gradient(
                90deg,
                rgba(0, 0, 0, 0.65) 0%,
                rgba(0, 0, 0, 0.28) 48%,
                rgba(0, 0, 0, 0) 78%
              );

            pointer-events: none;
          }

          .demirsan-banner-overlay .container {
            width: 100%;
          }

          .demirsan-banner-content {
            width: min(650px, 100%);
            color: #ffffff;
            pointer-events: auto;
          }

          .demirsan-banner-title {
            display: flex;
            flex-wrap: wrap;
            gap: 0 12px;

            margin: 0 0 18px;

            color: #ffffff;

            font-size: clamp(38px, 4.5vw, 68px);
            font-weight: 700;
            line-height: 1.12;
          }

          .demirsan-banner-title span {
            color: inherit;
          }

          .demirsan-banner-colorful-text {
            color: #e30613 !important;
          }

          .demirsan-banner-description {
            max-width: 580px;
            margin: 0 0 22px;

            color: rgba(255, 255, 255, 0.9);

            font-size: 18px;
            line-height: 1.65;
          }

          .demirsan-banner-editor {
            max-width: 580px;
            margin-bottom: 24px;

            color: rgba(255, 255, 255, 0.9);

            font-size: 17px;
            line-height: 1.65;
          }

          .demirsan-banner-editor p {
            margin: 0 0 12px;
            color: inherit;
          }

          .demirsan-banner-button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 10px;

            min-height: 50px;
            padding: 13px 24px;

            color: #ffffff !important;
            background: #e30613;

            border: 2px solid #e30613;
            border-radius: 6px;

            font-size: 15px;
            font-weight: 700;
            line-height: 1.2;
            text-decoration: none !important;

            transition:
              color 0.2s ease,
              background-color 0.2s ease,
              transform 0.2s ease;
          }

          .demirsan-banner-button::before,
          .demirsan-banner-button::after {
            display: none !important;
            content: none !important;
          }

          .demirsan-banner-button:hover {
            color: #e30613 !important;
            background: #ffffff;
            transform: translateY(-2px);
          }

          .demirsan-banner
            .swiper-button-prev,
          .demirsan-banner
            .swiper-button-next {
            width: 48px;
            height: 48px;

            border-radius: 50%;
            background: rgba(255, 255, 255, 0.88);

            box-shadow: 0 8px 22px
              rgba(0, 0, 0, 0.15);
          }

          .demirsan-banner
            .swiper-button-prev {
            left: 24px;
          }

          .demirsan-banner
            .swiper-button-next {
            right: 24px;
          }

          .demirsan-banner
            .swiper-button-prev::after,
          .demirsan-banner
            .swiper-button-next::after {
            color: #e30613;
            font-size: 18px;
            font-weight: 700;
          }

          .demirsan-banner-loading {
            width: 100%;
            height: 650px;
            background: #eeeeee;
          }

          @media (
            min-width: 768px
          ) and (
            max-width: 1199px
          ) {
            .demirsan-banner-slide,
            .demirsan-banner-loading {
              height: 480px;
            }

            .demirsan-banner-title {
              font-size: 44px;
            }

            .demirsan-banner-description,
            .demirsan-banner-editor {
              font-size: 16px;
            }
          }

          @media (max-width: 767px) {
            .demirsan-banner-slide,
            .demirsan-banner-loading {
              height: 320px;
            }

            .demirsan-banner-overlay {
              align-items: flex-end;
              padding: 24px 0;

              background:
                linear-gradient(
                  0deg,
                  rgba(0, 0, 0, 0.72) 0%,
                  rgba(0, 0, 0, 0.18) 65%,
                  transparent 100%
                );
            }

            .demirsan-banner-title {
              gap: 0 7px;
              margin-bottom: 10px;
              font-size: 28px;
            }

            .demirsan-banner-description {
              margin-bottom: 14px;
              font-size: 14px;
              line-height: 1.45;
            }

            .demirsan-banner-editor {
              display: none;
            }

            .demirsan-banner-button {
              min-height: 42px;
              padding: 9px 16px;
              font-size: 13px;
            }

            .demirsan-banner
              .swiper-button-prev,
            .demirsan-banner
              .swiper-button-next {
              width: 36px;
              height: 36px;
            }

            .demirsan-banner
              .swiper-button-prev {
              left: 10px;
            }

            .demirsan-banner
              .swiper-button-next {
              right: 10px;
            }

            .demirsan-banner
              .swiper-button-prev::after,
            .demirsan-banner
              .swiper-button-next::after {
              font-size: 14px;
            }
          }
        `}
      </style>

      <div
        className="demirsan-banner"
        id="banner"
      >
        <Swiper
          modules={[
            Navigation,
            Autoplay,
            A11y,
          ]}
          slidesPerView={1}
          spaceBetween={0}
          speed={800}
          loop={slides.length > 1}
          navigation={slides.length > 1}
          autoplay={
            slides.length > 1
              ? {
                  delay: 3000,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }
              : false
          }
        >
          {slides.map((slide, index) => (
            <SwiperSlide key={slide.id}>
              {renderSlide(slide, index)}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
}

export default BannerOne;